import type { ResponsePrepTime } from "@memorang/types";
import {
	type RefObject,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import type { AudioPlayerRef } from "../../AudioPlayerContainer";
import type { AudioRecorderContainerRef } from "../../AudioRecorderContainer";
import type { SessionVideoPlayerRef } from "../../SessionVideoPlayer";
import { useSessionAutoStartAllowed } from "../SessionAutoStartContext";

type StimulusPlayerRef = Pick<
	AudioPlayerRef,
	"play" | "pause" | "isEnded" | "isPlaying" | "isLoaded"
>;

export type PrepPhase =
	| "idle"
	| "initialDelay"
	| "audioPlaying"
	| "ttsPlaying"
	| "micDelay"
	| "recording"
	| "completed";

export type UsePrepSequenceArgs = {
	prepTime?: ResponsePrepTime;
	timeLimit?: number;
	hasAudioStimulus: boolean;
	hasVideoStimulus?: boolean;
	hasRecorder: boolean;
	audioPlayerRef?: RefObject<AudioPlayerRef | null>;
	videoPlayerRef?: RefObject<SessionVideoPlayerRef | null>;
	recorderRef?: RefObject<AudioRecorderContainerRef | null>;
	// Optional spoken-stem phase after the stimulus audio (or on its own when
	// there is no stimulus audio). onAudioEnded then fires after the TTS ends.
	hasTtsStimulus?: boolean;
	ttsPlayerRef?: RefObject<StimulusPlayerRef | null>;
	initialElapsedSeconds?: number;
	onAudioEnded?: () => void;
	onRecordingStarted?: () => void;
	onRecordingEnded?: () => void;
	enabled?: boolean;
	// Keep the audio phase alive until the player mounts (e.g. inside a
	// bottom sheet) instead of treating a missing player as audio-ended.
	waitForPlayer?: boolean;
};

export type UsePrepSequenceResult = {
	phase: PrepPhase;
	remainingPrepSeconds: number;
	showBanner: boolean;
};

const BANNER_THRESHOLD_SECONDS = 10;
const AUDIO_STALL_GRACE_SECONDS = 15;

// Numeric prep: recorder items spend it right before recording (post-audio),
// items without a recorder spend it up front, before audio/autoplay.
// Object prep keeps its explicit audio/microphone split.
const getInitialDelay = (
	prepTime: ResponsePrepTime | undefined,
	hasRecorder: boolean,
): number => {
	if (typeof prepTime === "number") {
		return hasRecorder ? 0 : prepTime;
	}
	return prepTime?.audioDelaySeconds ?? 0;
};

const getMicDelay = (
	prepTime: ResponsePrepTime | undefined,
	hasRecorder: boolean,
): number => {
	if (typeof prepTime === "number") {
		return hasRecorder ? prepTime : 0;
	}
	return prepTime?.microphoneDelaySeconds ?? 0;
};

type PhaseTransition = { phase: PrepPhase; remaining: number };

const pickInitialPhase = (
	prepTime: ResponsePrepTime | undefined,
	hasMediaStimulus: boolean,
	hasRecorder: boolean,
	hasTts: boolean,
	elapsedSeconds = 0,
): PhaseTransition => {
	const initialDelay = getInitialDelay(prepTime, hasRecorder);
	const remainingInitialDelay = Math.max(0, initialDelay - elapsedSeconds);
	if (remainingInitialDelay > 0) {
		return { phase: "initialDelay", remaining: remainingInitialDelay };
	}

	if (hasMediaStimulus) {
		return { phase: "audioPlaying", remaining: 0 };
	}
	if (hasTts) {
		return { phase: "ttsPlaying", remaining: 0 };
	}
	const elapsedAfterInitialDelay = Math.max(0, elapsedSeconds - initialDelay);
	const micDelay = getMicDelay(prepTime, hasRecorder);
	const remainingMicDelay = Math.max(0, micDelay - elapsedAfterInitialDelay);
	if (hasRecorder && remainingMicDelay > 0) {
		return { phase: "micDelay", remaining: remainingMicDelay };
	}
	if (hasRecorder) {
		return { phase: "recording", remaining: 0 };
	}
	return { phase: "completed", remaining: 0 };
};

const pickPostAudioPhase = (
	prepTime: ResponsePrepTime | undefined,
	hasRecorder: boolean,
): PhaseTransition => {
	if (!hasRecorder) {
		return { phase: "completed", remaining: 0 };
	}
	const micDelay = getMicDelay(prepTime, hasRecorder);
	if (micDelay > 0) {
		return { phase: "micDelay", remaining: micDelay };
	}
	return { phase: "recording", remaining: 0 };
};

const pickPostInitialDelayPhase = (
	prepTime: ResponsePrepTime | undefined,
	hasMediaStimulus: boolean,
	hasRecorder: boolean,
	hasTts: boolean,
): PhaseTransition => {
	if (hasMediaStimulus) {
		return { phase: "audioPlaying", remaining: 0 };
	}
	if (hasTts) {
		return { phase: "ttsPlaying", remaining: 0 };
	}
	return pickPostAudioPhase(prepTime, hasRecorder);
};

const usePrepSequence = ({
	prepTime,
	hasAudioStimulus,
	hasVideoStimulus = false,
	hasRecorder,
	audioPlayerRef,
	videoPlayerRef,
	recorderRef,
	hasTtsStimulus = false,
	ttsPlayerRef,
	initialElapsedSeconds = 0,
	onAudioEnded,
	onRecordingStarted,
	onRecordingEnded,
	enabled: enabledProp = true,
	waitForPlayer = false,
}: UsePrepSequenceArgs): UsePrepSequenceResult => {
	const autoStartAllowed = useSessionAutoStartAllowed();
	const enabled = enabledProp && autoStartAllowed;
	const hasMediaStimulus = hasAudioStimulus || hasVideoStimulus;
	const hasTts = hasTtsStimulus && Boolean(ttsPlayerRef);
	const getStimulusPlayer = useCallback(
		(): StimulusPlayerRef | undefined =>
			audioPlayerRef?.current ?? videoPlayerRef?.current ?? undefined,
		[audioPlayerRef, videoPlayerRef],
	);
	const [phase, setPhase] = useState<PrepPhase>("idle");
	const [remainingPrepSeconds, setRemainingPrepSeconds] = useState(0);

	const startedRef = useRef(false);
	const recordingStartedFiredRef = useRef(false);
	const recordingEndedFiredRef = useRef(false);
	const audioEndedFiredRef = useRef(false);
	const audioStallTicksRef = useRef(0);
	const playAttemptedRef = useRef(false);
	const ttsEndedFiredRef = useRef(false);
	const ttsStallTicksRef = useRef(0);
	const ttsPlayAttemptedRef = useRef(false);
	const observedRecordingActiveRef = useRef(false);

	const onAudioEndedRef = useRef(onAudioEnded);
	const onRecordingStartedRef = useRef(onRecordingStarted);
	const onRecordingEndedRef = useRef(onRecordingEnded);
	useEffect(() => {
		onAudioEndedRef.current = onAudioEnded;
		onRecordingStartedRef.current = onRecordingStarted;
		onRecordingEndedRef.current = onRecordingEnded;
	}, [onAudioEnded, onRecordingStarted, onRecordingEnded]);

	const applyTransition = useCallback((next: PhaseTransition) => {
		setPhase(next.phase);
		setRemainingPrepSeconds(next.remaining);
	}, []);

	// Exits media chain when all audio (stimulus and optional TTS) finishes.
	const finishMediaChain = useCallback(() => {
		onAudioEndedRef.current?.();
		applyTransition(pickPostAudioPhase(prepTime, hasRecorder));
	}, [prepTime, hasRecorder, applyTransition]);

	// Leaves the audio phase once. Used by both the end-of-audio poll and the
	// fallbacks (autoplay blocked / no player), so a failed play() still advances
	// the sequence instead of stalling.
	const completeAudioPhase = useCallback(() => {
		if (audioEndedFiredRef.current) {
			return;
		}
		audioEndedFiredRef.current = true;
		if (hasTts && !ttsEndedFiredRef.current) {
			applyTransition({ phase: "ttsPlaying", remaining: 0 });
			return;
		}
		finishMediaChain();
	}, [hasTts, applyTransition, finishMediaChain]);

	const completeTtsPhase = useCallback(() => {
		if (ttsEndedFiredRef.current) {
			return;
		}
		ttsEndedFiredRef.current = true;
		finishMediaChain();
	}, [finishMediaChain]);

	useEffect(() => {
		if (!enabled || startedRef.current) {
			return;
		}
		startedRef.current = true;
		applyTransition(
			pickInitialPhase(
				prepTime,
				hasMediaStimulus,
				hasRecorder,
				hasTts,
				initialElapsedSeconds,
			),
		);
	}, [
		enabled,
		prepTime,
		hasMediaStimulus,
		hasRecorder,
		hasTts,
		initialElapsedSeconds,
		applyTransition,
	]);

	useEffect(() => {
		if (phase !== "audioPlaying" || !enabled) {
			return;
		}
		audioEndedFiredRef.current = false;
		audioStallTicksRef.current = 0;
		playAttemptedRef.current = false;
		const player = getStimulusPlayer();
		if (!player) {
			if (!waitForPlayer) {
				completeAudioPhase();
			}
			return;
		}

		if (!player.isLoaded) {
			return;
		}
		playAttemptedRef.current = true;
		player.play().catch(() => {
			completeAudioPhase();
		});
	}, [phase, enabled, getStimulusPlayer, completeAudioPhase, waitForPlayer]);

	useEffect(() => {
		if (enabled || phase !== "audioPlaying") {
			return;
		}
		getStimulusPlayer()?.pause();
	}, [enabled, phase, getStimulusPlayer]);

	useEffect(() => {
		if (phase !== "ttsPlaying" || !enabled) {
			return;
		}
		// ttsEndedFiredRef intentionally not reset to prevent duplicate TTS or onAudioEnded calls.
		ttsStallTicksRef.current = 0;
		ttsPlayAttemptedRef.current = false;
		const player = ttsPlayerRef?.current;
		if (!player?.isLoaded) {
			return;
		}
		ttsPlayAttemptedRef.current = true;
		player.play().catch(() => {
			completeTtsPhase();
		});
	}, [phase, enabled, ttsPlayerRef, completeTtsPhase]);

	useEffect(() => {
		if (enabled || phase !== "ttsPlaying") {
			return;
		}
		ttsPlayerRef?.current?.pause();
	}, [enabled, phase, ttsPlayerRef]);

	useEffect(() => {
		if (!enabled || phase !== "recording" || recordingStartedFiredRef.current) {
			return;
		}
		recordingStartedFiredRef.current = true;
		recorderRef?.current?.start();
		onRecordingStartedRef.current?.();
	}, [enabled, phase, recorderRef]);

	useEffect(() => {
		if (phase !== "completed") {
			return;
		}
		if (
			hasRecorder &&
			recordingStartedFiredRef.current &&
			!recordingEndedFiredRef.current
		) {
			recordingEndedFiredRef.current = true;
			onRecordingEndedRef.current?.();
		}
	}, [phase, hasRecorder]);

	const tickInitialDelay = useCallback(
		(prev: number): number => {
			if (prev > 1) {
				return prev - 1;
			}
			const next = pickPostInitialDelayPhase(
				prepTime,
				hasMediaStimulus,
				hasRecorder,
				hasTts,
			);
			setPhase(next.phase);
			return next.remaining;
		},
		[prepTime, hasMediaStimulus, hasRecorder, hasTts],
	);

	const tickMicDelay = useCallback((prev: number): number => {
		if (prev > 1) {
			return prev - 1;
		}
		setPhase("recording");
		return 0;
	}, []);

	// Not loaded yet: keep waiting, don't count this as a stall.
	const attemptFirstPlay = useCallback(
		(player: StimulusPlayerRef) => {
			if (!player.isLoaded) {
				return;
			}
			playAttemptedRef.current = true;
			player.play().catch(() => {
				completeAudioPhase();
			});
		},
		[completeAudioPhase],
	);

	const pollAudioEnded = useCallback(() => {
		const player = getStimulusPlayer();
		if (!player) {
			if (!waitForPlayer) {
				completeAudioPhase();
			}
			return;
		}
		if (!playAttemptedRef.current) {
			attemptFirstPlay(player);
			return;
		}
		if (player.isEnded) {
			completeAudioPhase();
			return;
		}
		if (player.isPlaying) {
			audioStallTicksRef.current = 0;
			return;
		}
		// Audio not playing or ended—end phase if stalled to prevent hanging.
		audioStallTicksRef.current += 1;
		if (audioStallTicksRef.current >= AUDIO_STALL_GRACE_SECONDS) {
			completeAudioPhase();
		}
	}, [getStimulusPlayer, completeAudioPhase, waitForPlayer, attemptFirstPlay]);

	// TTS uses the stall grace to avoid indefinite wait; failing open also pauses playback.
	const failOpenTtsPhase = useCallback(() => {
		ttsPlayerRef?.current?.pause();
		completeTtsPhase();
	}, [ttsPlayerRef, completeTtsPhase]);

	const pollTtsEnded = useCallback(() => {
		const player = ttsPlayerRef?.current;
		if (!player?.isLoaded) {
			ttsStallTicksRef.current += 1;
			if (ttsStallTicksRef.current >= AUDIO_STALL_GRACE_SECONDS) {
				failOpenTtsPhase();
			}
			return;
		}
		if (!ttsPlayAttemptedRef.current) {
			ttsPlayAttemptedRef.current = true;
			player.play().catch(() => {
				completeTtsPhase();
			});
			return;
		}
		if (player.isEnded) {
			completeTtsPhase();
			return;
		}
		if (player.isPlaying) {
			ttsStallTicksRef.current = 0;
			return;
		}
		ttsStallTicksRef.current += 1;
		if (ttsStallTicksRef.current >= AUDIO_STALL_GRACE_SECONDS) {
			failOpenTtsPhase();
		}
	}, [ttsPlayerRef, completeTtsPhase, failOpenTtsPhase]);

	const pollRecordingEnded = useCallback(() => {
		const isRecording = recorderRef?.current?.isRecording;
		if (isRecording) {
			observedRecordingActiveRef.current = true;
			return;
		}
		if (
			recordingStartedFiredRef.current &&
			observedRecordingActiveRef.current
		) {
			setPhase("completed");
		}
	}, [recorderRef]);

	// 1 Hz timer: updates countdowns and checks audio/recorder, stops if disabled.
	useEffect(() => {
		if (!enabled || phase === "idle" || phase === "completed") {
			return;
		}
		const id = setInterval(() => {
			if (phase === "initialDelay") {
				setRemainingPrepSeconds(tickInitialDelay);
				return;
			}
			if (phase === "micDelay") {
				setRemainingPrepSeconds(tickMicDelay);
				return;
			}
			if (phase === "audioPlaying") {
				pollAudioEnded();
				return;
			}
			if (phase === "ttsPlaying") {
				pollTtsEnded();
				return;
			}
			if (phase === "recording") {
				pollRecordingEnded();
			}
		}, 1000);
		return () => clearInterval(id);
	}, [
		enabled,
		phase,
		tickInitialDelay,
		tickMicDelay,
		pollAudioEnded,
		pollTtsEnded,
		pollRecordingEnded,
	]);

	const initialDelay = getInitialDelay(prepTime, hasRecorder);
	const showBanner =
		phase === "initialDelay" && initialDelay >= BANNER_THRESHOLD_SECONDS;

	return {
		phase,
		remainingPrepSeconds,
		showBanner,
	};
};

export default usePrepSequence;
